import { deploymentBranding } from '@/config/deployment'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { TimeRange, Theme, User, Notification, OrgBranding } from '@/types'
import type { AppNotification as ApiNotification } from '@/api/notifications'

interface AppNotification extends Notification {
  read: boolean
  createdAt?: string
  link?: string
  resourceType?: string
  resourceId?: string
}

interface AppState {
  // Auth
  user: User | null
  token: string | null
  setAuth: (user: User, token: string) => void
  // Swap in a refreshed token for the same signed-in user, leaving `user`
  // untouched — replacing it re-renders and can unmount the whole app.
  setToken: (token: string) => void
  updateCurrentUser: (updates: Partial<User>) => void
  clearAuth: () => void

  // Theme
  theme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void

  // Sidebar
  sidebarCollapsed: boolean
  toggleSidebar: () => void

  // Time range
  timeRange: TimeRange
  setTimeRange: (range: TimeRange) => void

  // Global refresh trigger — increment to force all panels to re-fetch
  refreshCounter: number
  triggerRefresh: () => void

  // Command palette
  commandPaletteOpen: boolean
  openCommandPalette: () => void
  closeCommandPalette: () => void

  // Notifications
  notifications: AppNotification[]
  setNotifications: (items: ApiNotification[]) => void
  addNotification: (n: Notification) => void
  markRead: (id: string) => void
  markAllRead: () => void

  // Branding
  branding: Partial<OrgBranding>
  setBranding: (b: Partial<OrgBranding>) => void
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // Auth
      user: null,
      token: null,
      setAuth: (user, token) => set({ user, token }),
      setToken: (token) => set({ token }),
      updateCurrentUser: (updates) => set(state => ({
        user: state.user ? { ...state.user, ...updates } : null,
      })),
      clearAuth: () => set({ user: null, token: null }),

      // Theme
      theme: 'light',
      setTheme: (theme) => {
        set({ theme })
        applyTheme(theme)
      },
      toggleTheme: () => set(state => {
        const next = state.theme === 'dark' ? 'light' : 'dark'
        applyTheme(next)
        return { theme: next }
      }),

      // Sidebar
      sidebarCollapsed: false,
      toggleSidebar: () => set(state => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      // Time range
      timeRange: { from: 'now-6h', to: 'now' },
      setTimeRange: (timeRange) => set({ timeRange }),

      // Global refresh
      refreshCounter: 0,
      triggerRefresh: () => set(state => ({ refreshCounter: state.refreshCounter + 1 })),

      // Command palette
      commandPaletteOpen: false,
      openCommandPalette: () => set({ commandPaletteOpen: true }),
      closeCommandPalette: () => set({ commandPaletteOpen: false }),

      // Notifications
      notifications: [],
      setNotifications: (items) => set({
        notifications: items.map(n => ({
          id: n.id,
          type: n.type,
          title: n.title,
          message: n.message,
          read: !!n.readAt,
          createdAt: n.createdAt,
          link: n.link,
          resourceType: n.resourceType,
          resourceId: n.resourceId,
        }))
      }),
      addNotification: (n) => set(state => ({
        notifications: [{ ...n, read: false }, ...state.notifications].slice(0, 50)
      })),
      markRead: (id) => set(state => ({
        notifications: state.notifications.map(n => n.id === id ? { ...n, read: true } : n)
      })),
      markAllRead: () => set(state => ({
        notifications: state.notifications.map(n => ({ ...n, read: true }))
      })),

      // Branding
      branding: deploymentBranding({}),
      setBranding: (b) => {
        set({ branding: deploymentBranding(b) })
        applyBranding(b)
      },
    }),
    {
      name: 'multiaccess-app',
      partialize: (state) => ({
        theme: state.theme,
        sidebarCollapsed: state.sidebarCollapsed,
        timeRange: state.timeRange,
        token: state.token,
        user: state.user,
      }),
    }
  )
)

function applyTheme(theme: Theme) {
  const root = document.documentElement
  if (theme === 'dark') root.classList.add('dark')
  else root.classList.remove('dark')
}

export function applyBranding(b: Partial<OrgBranding>) {
  b = deploymentBranding(b)
  const root = document.documentElement

  const c = b.colors
  if (c) {
    // Parse hex → [r, g, b]
    const hexToRgb = (h: string): [number, number, number] | null => {
      const m = h.replace('#', '').match(/.{2}/g)
      return m && m.length >= 3
        ? [parseInt(m[0], 16), parseInt(m[1], 16), parseInt(m[2], 16)]
        : null
    }
    const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)))
    const mix   = (a: number, b: number, t: number) => clamp(a + (b - a) * t)
    const rgb   = (r: number, g: number, bl: number) => `${r} ${g} ${bl}`
    const setProp = (name: string, hex: string) => {
      const v = hexToRgb(hex)
      if (v) root.style.setProperty(name, rgb(...v))
    }

    // ── Sidebar ──────────────────────────────────────────────────────────────
    setProp('--sidebar-bg',          c.sidebar)
    setProp('--sidebar-text',        c.sidebarText)
    setProp('--sidebar-text-active', '#ffffff')

    // Hover = sidebar bg lightened 12% toward white
    const sbRgb = hexToRgb(c.sidebar)
    if (sbRgb) {
      const [sr, sg, sb] = sbRgb
      root.style.setProperty('--sidebar-hover', rgb(mix(sr, 255, 0.12), mix(sg, 255, 0.12), mix(sb, 255, 0.12)))
    }

    // Accent = use accent color if provided, else fall back to primary
    const accentHex = c.accent ?? c.primary
    const acRgb = hexToRgb(accentHex)
    if (acRgb) root.style.setProperty('--sidebar-accent', rgb(...acRgb))

    // ── Brand color scale ─────────────────────────────────────────────────────
    // Generate all 10 shades from the primary hex so every Tailwind
    // brand-* utility (bg-brand-500, hover:bg-brand-600, text-brand-400, etc.)
    // picks up the new color immediately — no page reload required.
    const pr = hexToRgb(c.primary)
    if (pr) {
      const [r, g, bl] = pr
      // Lighter shades: mix toward white
      root.style.setProperty('--brand-50',  rgb(mix(r, 255, 0.96), mix(g, 255, 0.96), mix(bl, 255, 0.96)))
      root.style.setProperty('--brand-100', rgb(mix(r, 255, 0.90), mix(g, 255, 0.90), mix(bl, 255, 0.90)))
      root.style.setProperty('--brand-200', rgb(mix(r, 255, 0.80), mix(g, 255, 0.80), mix(bl, 255, 0.80)))
      root.style.setProperty('--brand-300', rgb(mix(r, 255, 0.62), mix(g, 255, 0.62), mix(bl, 255, 0.62)))
      root.style.setProperty('--brand-400', rgb(mix(r, 255, 0.30), mix(g, 255, 0.30), mix(bl, 255, 0.30)))
      // Base shade
      root.style.setProperty('--brand-500', rgb(r, g, bl))
      // Darker shades: mix toward black
      root.style.setProperty('--brand-600', rgb(mix(r, 0, 0.15), mix(g, 0, 0.15), mix(bl, 0, 0.15)))
      root.style.setProperty('--brand-700', rgb(mix(r, 0, 0.30), mix(g, 0, 0.30), mix(bl, 0, 0.30)))
      root.style.setProperty('--brand-800', rgb(mix(r, 0, 0.50), mix(g, 0, 0.50), mix(bl, 0, 0.50)))
      root.style.setProperty('--brand-900', rgb(mix(r, 0, 0.70), mix(g, 0, 0.70), mix(bl, 0, 0.70)))

      root.style.setProperty('--chart-1', rgb(r, g, bl))
      root.style.setProperty('--chart-3', rgb(mix(r, 255, 0.30), mix(g, 255, 0.30), mix(bl, 255, 0.30)))
      root.style.setProperty('--chart-5', rgb(mix(r, 0, 0.18), mix(g, 0, 0.18), mix(bl, 0, 0.18)))
    }
    if (acRgb) {
      const [ar, ag, ab] = acRgb
      root.style.setProperty('--chart-2', rgb(ar, ag, ab))
      root.style.setProperty('--chart-4', rgb(mix(ar, 255, 0.32), mix(ag, 255, 0.32), mix(ab, 255, 0.32)))
      root.style.setProperty('--chart-6', rgb(mix(ar, 0, 0.20), mix(ag, 0, 0.20), mix(ab, 0, 0.20)))
    }

    // Dark-mode primary (applied when .dark class is on <html>)
    if (c.primaryDark) {
      const pd = hexToRgb(c.primaryDark)
      if (pd) {
        // Inject a <style> that overrides --brand-500 inside .dark scope
        const DARK_STYLE_ID = 'multiaccess-brand-dark'
        let el = document.getElementById(DARK_STYLE_ID) as HTMLStyleElement | null
        if (!el) {
          el = document.createElement('style')
          el.id = DARK_STYLE_ID
          document.head.appendChild(el)
        }
        el.textContent = `.dark { --brand-500: ${rgb(...pd)}; }`
      }
    }
  }

  // Page title
  if (b.productName) document.title = b.productName

  // Favicon
  if (b.faviconUrl) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = b.faviconUrl
  }

  // Custom CSS injection
  const STYLE_ID = 'multiaccess-custom-css'
  let styleEl = document.getElementById(STYLE_ID) as HTMLStyleElement | null
  if (b.customCss) {
    if (!styleEl) {
      styleEl = document.createElement('style')
      styleEl.id = STYLE_ID
      document.head.appendChild(styleEl)
    }
    styleEl.textContent = b.customCss
  } else if (styleEl) {
    styleEl.textContent = ''
  }
}

// Init theme on module load
const stored = JSON.parse(localStorage.getItem('multiaccess-app') ?? '{}')
applyTheme(stored?.state?.theme ?? 'light')
